import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"
import { merge } from "lodash-es"

import { callMutation } from "shared/api/callMutation"
import { MutationTypename } from "shared/api/types"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import {
  TeamMember,
  UpdateTeamMemberInput
} from "shared/types/__generated__/graphql"

import { useTeamLazyQuery } from "entities/teams/graphql/queries.generated"

import {
  UpdateTeamMemberDocument,
  UpdateTeamMemberMutation,
  UpdateTeamMemberMutationVariables
} from "../graphql/mutations.generated"
import { useTeamMembersLazyQuery } from "../graphql/queries.generated"
import { readTeamMember } from "../lib/readTeamMember"

export function useUpdateTeamMember() {
  const { cache } = useApolloClient()
  const { t } = useTranslation()
  const [refetchTeam] = useTeamLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const [refetchTeamMembers] = useTeamMembersLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.TeamMember.UpdateTeamMember.Start"),
      success: t("Request.TeamMember.UpdateTeamMember.Success"),
      failure: t("Request.TeamMember.UpdateTeamMember.Failure")
    }
  })

  const [apolloMutate, mutationState] = useApolloMutation<
    UpdateTeamMemberMutation,
    UpdateTeamMemberMutationVariables
  >(UpdateTeamMemberDocument)

  const handleUpdateTeamMember = useEvent(
    async (
      {
        teamID,
        teamMemberID,
        updates
      }: {
        teamID: string
        teamMemberID: string
        updates: UpdateTeamMemberInput
      },
      options?: { suppressToasts?: boolean }
    ): Promise<ApiResponse<TeamMember>> => {
      const existingData = readTeamMember(teamMemberID, cache)
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: !!options?.suppressToasts
      })

      const optimisticResponse: UpdateTeamMemberMutation | undefined =
        existingData
          ? {
              teamMember: merge({}, existingData, updates),
              __typename: MutationTypename
            }
          : undefined

      return callMutation(apolloMutate, {
        variables: {
          input: updates,
          teamMemberID,
          teamID
        },
        transform: data => data.teamMember as TeamMember,
        apolloOptions: {
          optimisticResponse,
          onCompleted: () => {
            raiseSuccessToast()
          },
          onError: () => {
            refetchTeamMembers({ variables: { teamID } })
            refetchTeam({ variables: { id: teamID } })
            raiseErrorToast()
          }
        }
      })
    }
  )

  return {
    updateTeamMember: handleUpdateTeamMember,
    ...mutationState
  }
}
